/**
 * PURE display derivations for the v2 detail page components — plain `.ts`,
 * client-safe, no db/server imports (same rule as types.ts). Components keep
 * zero parsing logic; everything here takes DTO fields and returns strings.
 */
import { extractPredictions } from '#app/utils/report-data-helpers'
import type {
	DerivedApplicationStatus,
	IdVerificationFacts,
	OpenroomRecordRef,
	OpenroomSummary,
	ScreeningKind,
	VeriffDocumentRef,
} from './types'

const formatNumber = (value: number): string =>
	value.toLocaleString('en-US', { maximumFractionDigits: 0 })

export interface SummaryTileValues {
	/** e.g. '$1,800 – $2,200 /mo' (design §5), null when the AI report lacks it. */
	rentRange: string | null
	/** e.g. '$45,000', null when the AI report lacks it. */
	savings: string | null
}

/** Tile values from the opaque AI report payload (data-map §4: the report
 * stays opaque in the DTO; derivation happens client-side, v1 parity with
 * report-data-helpers deriveReportData). */
export function deriveSummaryTileValues(
	aiReportData: Record<string, unknown> | null | undefined,
): SummaryTileValues {
	const predictions = extractPredictions(aiReportData ?? null)
	const rentRange = predictions?.applicant?.rentRange
	const savings = predictions?.financialSummary?.savingsInvestments
	return {
		rentRange: rentRange
			? `$${formatNumber(rentRange.min)} – $${formatNumber(rentRange.max)} /mo`
			: null,
		savings: typeof savings === 'number' ? `$${formatNumber(savings)}` : null,
	}
}

/** The AI report has produced predictions — v1's `reportData` gate: the
 * processing overlay shows only while this is false (v1:7057-7072). */
export function hasAiPredictions(
	aiReportData: Record<string, unknown> | null | undefined,
): boolean {
	return extractPredictions(aiReportData ?? null) != null
}

/** Which surface the report area shows (DETAIL-PAGE-V2-PARITY §9 item 2):
 * - `report`     — the AI report has predictions (render the report sections,
 *                  even while still processing — v1 keeps showing it, :7057).
 * - `processing` — generating, no predictions yet → progress overlay.
 * - `awaiting`   — no report and nothing processing: the applicant hasn't
 *                  completed screening, or generation failed → empty-state card
 *                  instead of empty `-`/"Not Available" fields.
 * - `skipped`    — student Bank=No path (`aiProcessingStatus === 'skipped'`):
 *                  no AI report forever; show summary tiles with dashes +
 *                  ID-only verification (no awaiting / score / upsell).
 * Step-data sections render independently of this. */
export type DetailReportView = 'report' | 'processing' | 'awaiting' | 'skipped'

export function deriveDetailReportView(input: {
	isProcessing: boolean
	hasReport: boolean
	/** Student Bank=No intentional no-AI path. */
	skipped?: boolean
}): DetailReportView {
	if (input.skipped) return 'skipped'
	if (input.hasReport) return 'report'
	return input.isProcessing ? 'processing' : 'awaiting'
}

/** 'Dec 15, 2025, 04:45 PM' — the timeline panel's timestamp format
 * (design §15: hours are zero-padded), rendered in the app's default
 * Toronto timezone. */
export function formatEventTimestamp(iso: string | null): string | null {
	if (!iso) return null
	const date = new Date(iso)
	if (Number.isNaN(date.getTime())) return null
	return date.toLocaleString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
		timeZone: 'America/Toronto',
	})
}

/**
 * The applicant's face photo among the Veriff documents — v1-parity LOOSE
 * match (v1@4cbfe7e:5918): keyed 'face-pre' by name, OR carried with
 * context 'face' and an image mimetype. Shared by the header avatar and the
 * ID-verification modal so both resolve the same photo.
 */
export function findFacePhoto(
	veriffDocuments: VeriffDocumentRef[],
): VeriffDocumentRef | undefined {
	return veriffDocuments.find(
		(doc) =>
			doc.name === 'face-pre' ||
			(doc.context === 'face' && doc.mimetype.startsWith('image/')),
	)
}

/** Veriff document-type enum → human label (Veriff decision schema). Unknown
 * codes are title-cased from their underscore form ('FOO_BAR' → 'Foo Bar'). */
const DOCUMENT_TYPE_LABELS: Record<string, string> = {
	PASSPORT: 'Passport',
	DRIVERS_LICENSE: "Driver's License",
	ID_CARD: 'ID Card',
	RESIDENCE_PERMIT: 'Residence Permit',
	VISA: 'Visa',
}

/** ISO country code → demonym, for the 'Canadian Passport' label form. Only the
 * countries we commonly see; an unknown code drops the prefix (just the type). */
const COUNTRY_DEMONYMS: Record<string, string> = {
	CA: 'Canadian',
	US: 'American',
	GB: 'British',
	IN: 'Indian',
	AU: 'Australian',
	NZ: 'New Zealand',
	IE: 'Irish',
	FR: 'French',
	DE: 'German',
	NG: 'Nigerian',
	PH: 'Philippine',
	MX: 'Mexican',
	BR: 'Brazilian',
	CN: 'Chinese',
	PK: 'Pakistani',
}

const MONTHS_SHORT = [
	'Jan',
	'Feb',
	'Mar',
	'Apr',
	'May',
	'Jun',
	'Jul',
	'Aug',
	'Sep',
	'Oct',
	'Nov',
	'Dec',
]

function formatDocumentTypeLabel(
	type: unknown,
	country: unknown,
): string | null {
	if (typeof type !== 'string' || !type.trim()) return null
	const code = type.trim().toUpperCase()
	const label =
		DOCUMENT_TYPE_LABELS[code] ??
		code
			.split('_')
			.map((w) => w.charAt(0) + w.slice(1).toLowerCase())
			.join(' ')
	const demonym =
		typeof country === 'string'
			? COUNTRY_DEMONYMS[country.trim().toUpperCase()]
			: undefined
	return demonym ? `${demonym} ${label}` : label
}

/** 'Mar 2029' from an ISO-ish expiry. Reads the year/month off a YYYY-MM[-DD]
 * string directly (no Date → no timezone drift), falling back to Date parsing
 * for other shapes. null when unparseable/absent. */
function formatDocumentExpiry(validUntil: unknown): string | null {
	if (typeof validUntil !== 'string' || !validUntil.trim()) return null
	const iso = validUntil.trim().match(/^(\d{4})-(\d{2})/)
	if (iso) {
		const monthIndex = Number(iso[2]) - 1
		if (monthIndex >= 0 && monthIndex < 12) {
			return `${MONTHS_SHORT[monthIndex]} ${iso[1]}`
		}
	}
	const date = new Date(validUntil)
	if (Number.isNaN(date.getTime())) return null
	return `${MONTHS_SHORT[date.getMonth()]} ${date.getFullYear()}`
}

/** Veriff `verification.person` → "First Last"; null when it carries neither. */
function formatVeriffPersonName(
	person: Record<string, unknown> | null | undefined,
): string | null {
	if (!person) return null
	const part = (value: unknown) =>
		typeof value === 'string' ? value.trim() : ''
	const full = [part(person.firstName), part(person.lastName)]
		.filter(Boolean)
		.join(' ')
	return full || null
}

/**
 * Full Name / Document Type / Expiry Date header facts for the
 * ID-verification modal, from the Veriff record's `verification.document` and
 * `verification.person` objects. Pure so the field paths and formatting are
 * unit-tested independently of the db read. Any field is null when the
 * payload omits it (the tile renders '-').
 *
 * The name deliberately comes from the ID rather than the account: the modal
 * answers "what does the verified document say", and one of the AI risk flags
 * is precisely that the two disagree — falling back to the applicant's own
 * name would hide that.
 */
export function deriveIdVerificationFacts(
	document: Record<string, unknown> | null | undefined,
	person?: Record<string, unknown> | null,
): IdVerificationFacts {
	const documentName = formatVeriffPersonName(person)
	if (!document) {
		return { documentName, documentType: null, documentExpiry: null }
	}
	return {
		documentName,
		documentType: formatDocumentTypeLabel(document.type, document.country),
		documentExpiry: formatDocumentExpiry(document.validUntil),
	}
}

/** v1's success statuses (VerificationSummaryContent resolveStatusTone). */
export function isVerifiedStatus(status: string | undefined): boolean {
	if (!status) return false
	const lower = status.toLowerCase()
	return (
		lower === 'completed' ||
		lower === 'verified' ||
		lower === 'available' ||
		lower === 'approved'
	)
}

export type VerificationBarTone = 'green' | 'amber' | 'red' | 'neutral'

/**
 * Verification status → top-bar tone. The SINGLE rule shared by the detail
 * page AND the combined report, and kept identical to the PDF
 * (`pdf/theme.ts` `verificationKind` + its `toneOf`). One mapping so the SAME
 * status never shows a different colour across pre-screening / full screening /
 * combined / PDF (user 2026-06-16 — overrides the spec's pre-screen "green-only"
 * bars; FULLSCREEN-DESIGN-SPEC §6):
 *   - completed / verified / available          → green
 *   - inconsistent / pending / in_progress / not_included → amber
 *     (a partial/caution match, still in progress, or deliberately excluded —
 *     never a failure)
 *   - not applicable                             → neutral (does not apply to
 *     this applicant — grey, not a warning; user 2026-07-16)
 *   - any other PRESENT status ("Not Completed", "failed", …) → red
 *   - status absent/blank                        → neutral ("Not Available")
 */
export function verificationBarTone(
	status: string | null | undefined,
): VerificationBarTone {
	if (status == null || status.trim() === '') return 'neutral'
	if (isVerifiedStatus(status)) return 'green'
	const lower = status.toLowerCase()
	// Deliberately not applicable to this applicant — grey, never a warning.
	if (lower === 'not applicable') return 'neutral'
	if (
		lower === 'inconsistent' ||
		lower === 'pending' ||
		lower === 'in_progress' ||
		lower === 'in progress' ||
		lower === 'not_included' ||
		lower === 'not included'
	)
		return 'amber'
	return 'red'
}

export interface CourtRecordsTileView {
	barTone: VerificationBarTone
	text: string
	url: string | null
}

/**
 * Court Records tile view (data-map §4 status chain, v1:5517-5547 display
 * strings): paused → neutral; records found → amber + the court-order url;
 * pending → amber; failed → red; completed-clean → green. A missing summary
 * renders like the other tiles' unavailable state. Shared by the individual
 * tile and the combined household tile (which passes the household rollup
 * url-less — its popover carries the per-member links).
 */
export function courtRecordsTileView(
	openroom: Pick<OpenroomSummary, 'status' | 'url'> | null,
): CourtRecordsTileView {
	if (!openroom) return { barTone: 'neutral', text: 'Not Available', url: null }
	switch (openroom.status) {
		case 'paused':
			return { barTone: 'neutral', text: 'Paused', url: null }
		case 'records_found':
			return { barTone: 'amber', text: 'Records Found', url: openroom.url }
		case 'pending':
			return { barTone: 'amber', text: 'Pending', url: null }
		case 'failed':
			return { barTone: 'red', text: 'Failed', url: null }
		case 'completed':
			return { barTone: 'green', text: 'No Records', url: null }
	}
}

/** The record cards for the Court Records dialog: `records` is non-empty
 * only for records_found; fall back to the single primary url so a lone
 * court-order link still renders one card. */
export function courtRecordRefs(
	openroom: OpenroomSummary | null | undefined,
): OpenroomRecordRef[] {
	if (openroom?.records && openroom.records.length > 0) return openroom.records
	return openroom?.url ? [{ url: openroom.url }] : []
}

export interface VerificationTileState {
	verified: boolean
	/** The AI report produced this section at all. */
	present: boolean
	/** Raw section status — fed to {@link verificationBarTone} for the bar. */
	status: string | undefined
}

export interface VerificationTileStates {
	id: VerificationTileState
	income: VerificationTileState
	rent: VerificationTileState
}

export function deriveVerificationTiles(
	aiReportData: Record<string, unknown> | null | undefined,
): VerificationTileStates {
	const summary = extractPredictions(aiReportData ?? null)?.verificationSummary
	const state = (section?: { status?: string }): VerificationTileState => ({
		verified: false,
		present: Boolean(section),
		status: section?.status,
	})
	return {
		id: state(summary?.idVerification),
		income: state(summary?.incomeVerification),
		rent: state(summary?.rentVerification),
	}
}

/**
 * When Bank=No skips AI, the shared VerificationSummary still needs the same
 * `predictions.verificationSummary.idVerification` shape a normal report
 * carries — otherwise the existing ID tile path shows Not Available.
 */
export function idVerificationAiStub(input: {
	idStep?: {
		status?: string
		verificationStatus?: string
		completed?: boolean
	} | null
	hasVeriffDocuments: boolean
}): Record<string, unknown> | null {
	const stepStatus =
		input.idStep?.verificationStatus ||
		input.idStep?.status ||
		(input.idStep?.completed ? 'completed' : undefined)
	const stepDone =
		Boolean(input.idStep?.completed) ||
		isVerifiedStatus(stepStatus) ||
		stepStatus?.toLowerCase() === 'approved'
	if (!stepDone && !input.hasVeriffDocuments) return null
	return {
		predictions: {
			verificationSummary: {
				idVerification: { status: 'Completed', details: '' },
			},
		},
	}
}

/** Port of v1 PersonalDetailsSection splitName + capitalizeWords. */
export function splitName(fullName: string): {
	firstName: string
	lastName: string
} {
	const capitalize = (str: string) =>
		str
			.split(' ')
			.map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
			.join(' ')
	const parts = fullName.trim().split(/\s+/).filter(Boolean)
	if (parts.length === 0) return { firstName: '', lastName: '' }
	if (parts.length === 1)
		return { firstName: capitalize(parts[0]), lastName: '' }
	return {
		firstName: capitalize(parts[0]),
		lastName: capitalize(parts.slice(1).join(' ')),
	}
}

const APPLICATION_TYPE_LABELS = {
	primary: 'Primary Applicant',
	co_applicant: 'Co-Applicant',
	guarantor: 'Guarantor',
} as const

export function applicationTypeLabel(
	type: keyof typeof APPLICATION_TYPE_LABELS,
): string {
	return APPLICATION_TYPE_LABELS[type]
}

/** Property-strip status pill (design §3 'Pre-Screen' dark pill). */
export const SCREENING_KIND_LABELS: Record<ScreeningKind, string> = {
	pre_screening: 'Pre-Screen',
	full: 'Full Screen',
}

/** '4 years' from a date of birth; 'Infant' under 1; null when unparseable. */
export function childAgeLabel(
	dateOfBirth: string | undefined,
	now: Date,
): string | null {
	if (!dateOfBirth) return null
	const dob = new Date(dateOfBirth)
	if (Number.isNaN(dob.getTime())) return null
	let years = now.getFullYear() - dob.getFullYear()
	const monthDelta = now.getMonth() - dob.getMonth()
	if (monthDelta < 0 || (monthDelta === 0 && now.getDate() < dob.getDate())) {
		years--
	}
	if (years < 0) return null
	if (years < 1) return 'Infant'
	return `${years} ${years === 1 ? 'year' : 'years'}`
}

/** 'yes'/'no' step strings → 'Yes'/'No'; other values pass through; '-' gap. */
export function yesNoLabel(value: string | undefined): string {
	if (!value) return '-'
	const lower = value.toLowerCase()
	if (lower === 'yes') return 'Yes'
	if (lower === 'no') return 'No'
	return value
}

/**
 * Additional Questions prompt → Title Case for the FieldBox label (report
 * page QA 2026-09-08: Figma capitalises every word, e.g. "Have You Ever Been
 * Evicted?"). Only the first letter of each word is touched, so acronyms
 * ("N4", "LMR") and letters after an apostrophe ("don't") keep their
 * shape; words after an opening paren / slash / hyphen count as words
 * ("move-in" → "Move-In")
 * ("(scholarships, grants, loans)" → "(Scholarships, Grants, Loans)").
 * Landlord-authored custom prompts (any casing) go through the same rule.
 */
export function questionLabel(prompt: string): string {
	return prompt.replace(
		/(^|[\s(\/\-\u2013\u2014])([a-z])/g,
		(_match, lead: string, letter: string) => `${lead}${letter.toUpperCase()}`,
	)
}

/** '$ 125,000' for numeric-ish step strings; raw passthrough otherwise. */
export function formatMoneyValue(value: string | undefined): string {
	if (!value) return '-'
	const numeric = Number(value.replace(/[$,\s]/g, ''))
	if (!Number.isFinite(numeric) || value.replace(/[$,\s]/g, '') === '') {
		return value
	}
	return `$ ${formatNumber(numeric)}`
}

/** '3' → '3 years' (design §8 Tenure); non-numeric strings pass through. */
export function tenureLabel(value: string | undefined): string {
	if (!value) return '-'
	const numeric = Number(value)
	if (!Number.isFinite(numeric)) return value
	return `${value} ${numeric === 1 ? 'year' : 'years'}`
}

/** Work-authorization statuses that carry an expiration date. The screening
 * wizard only collects "Expiration of WAS" for these three visa types, so the
 * detail page and PDF must hide any (stale) expiration for Permanent Resident /
 * Canadian Citizen / Refugee — e.g. left behind after switching away from a
 * visa (TT2-509). Case-insensitive so legacy rows still match. */
export function workAuthStatusHasExpiration(
	status: string | null | undefined,
): boolean {
	if (!status) return false
	const normalized = status.trim().toLowerCase()
	return (
		normalized === 'student visa' ||
		normalized === 'work visa' ||
		normalized === 'visitor visa'
	)
}

/**
 * Pet weight label. The PropertyStep dropdown stores the unit already
 * ('10–20 lbs', '+30 lbs'), while the legacy free-text input stores a bare
 * number ('25'). Append 'lbs' only when the value doesn't already carry a unit
 * so we never render 'lbs lbs'. Returns null for empty input.
 */
export function formatPetWeight(
	weight: string | number | undefined,
): string | null {
	const trimmed = String(weight ?? '').trim()
	if (!trimmed) return null
	return /lbs?\b/i.test(trimmed) ? trimmed : `${trimmed} lbs`
}

/**
 * Display casing for a picked FORM OPTION (employment type, payment method,
 * living situation, work-authorization status …) — never for free text like
 * names, addresses or emails.
 *
 * The screening form writes properly-cased options today ('Part-Time',
 * '(PAD) Pre-Authorized Debit'), but older rows hold the raw slug
 * ('part-time', 'e-transfer'), so a card could show either. Title-case ONLY
 * when the value carries no capital of its own: anything already cased is the
 * author's intent and is left alone, which keeps acronyms like '(PAD)' and
 * 'Roommates (Not On Lease)' intact. Separators are preserved, so 'e-transfer'
 * reads 'E-Transfer' and 'n/a' reads 'N/A'.
 */
export function optionLabel(value: string | undefined): string | undefined {
	const trimmed = value?.trim()
	if (!trimmed) return undefined
	if (/[A-Z]/.test(trimmed)) return trimmed
	const known = HYPHENATED_OPTIONS[trimmed.toLowerCase()]
	if (known) return known
	// Everything else reads as a phrase slug, so the hyphen is a word gap
	// ('living-with-family' → 'Living With Family'), matching rentalStatusLabel.
	return trimmed
		.replace(/[-_]+/g, ' ')
		.split(/([\s/]+)/)
		.map((part) =>
			/^[\s/]+$/.test(part)
				? part
				: part.charAt(0).toUpperCase() + part.slice(1),
		)
		.join('')
}

/** Legacy slugs whose hyphen belongs to the TERM, not to a word gap — mapped
 * to the exact strings the screening form writes today so old and new rows
 * read identically. */
const HYPHENATED_OPTIONS: Record<string, string> = {
	'full-time': 'Full-Time',
	'part-time': 'Part-Time',
	'self-employed': 'Self-Employed',
	'e-transfer': 'E-Transfer',
	'pre-authorized debit': 'Pre-Authorized Debit',
}

/** PropertyStep pet-type slugs → their card labels ('dog' → 'Dog'). Unknown
 * slugs (legacy free-form values) title-case; null when unset so callers can
 * fall back (e.g. to a legacy `breed`). */
export function petTypeLabel(type: string | undefined): string | null {
	if (!type || !type.trim()) return null
	const trimmed = type.trim()
	return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase()
}

/** PropertyStep vehicle-type slugs → their card labels. Unknown slugs (legacy
 * free-form values) title-case; null when unset so callers skip the suffix. */
const VEHICLE_TYPE_LABELS: Record<string, string> = {
	car: 'Car / SUV',
	motorbike: 'Motorbike',
	truck: 'Truck / Van',
}

export function vehicleTypeLabel(type: string | undefined): string | null {
	if (!type || !type.trim()) return null
	const trimmed = type.trim()
	return (
		VEHICLE_TYPE_LABELS[trimmed.toLowerCase()] ??
		trimmed.charAt(0).toUpperCase() + trimmed.slice(1)
	)
}

/** A property-step vehicle entry. The current screening flow stores
 * `vehicleType` + `registration`; older applications carry make/model/plate. */
export interface VehicleEntry {
	vehicleType?: string
	registration?: string
	make?: string
	model?: string
	plate?: string
}

/** The vehicle's registration number — `registration` (current flow) first,
 * then the legacy `plate`, then 'make model' as the last-resort legacy value.
 * Null when the entry carries nothing usable (callers render their own gap). */
export function vehicleValueLabel(vehicle: VehicleEntry): string | null {
	const registration = vehicle.registration?.trim() || vehicle.plate?.trim()
	if (registration) return registration
	const makeModel = [vehicle.make, vehicle.model]
		.map((part) => part?.trim())
		.filter(Boolean)
		.join(' ')
	return makeModel || null
}

/** Applicant-provided guarantor, normalized for the Request Guarantor dialog's
 * "found from onboarding" pre-fill (property step's `guarantor`). Splits a
 * single `name` into first/last when the discrete fields are absent. Returns
 * null when nothing usable was provided. */
export interface ProvidedGuarantor {
	firstName: string
	lastName: string
	relationship: string
	email: string
	phone: string
}

export function deriveProvidedGuarantor(
	guarantor:
		| {
				name?: string
				firstName?: string
				lastName?: string
				email?: string
				phone?: string
				relationship?: string
		  }
		| undefined,
): ProvidedGuarantor | null {
	if (!guarantor) return null
	let firstName = guarantor.firstName?.trim() ?? ''
	let lastName = guarantor.lastName?.trim() ?? ''
	const fullName = guarantor.name?.trim() ?? ''
	if (!firstName && !lastName && fullName) {
		const parts = fullName.split(/\s+/).filter(Boolean)
		firstName = parts[0] ?? ''
		lastName = parts.slice(1).join(' ')
	}
	const email = guarantor.email?.trim() ?? ''
	const phone = guarantor.phone?.trim() ?? ''
	const relationship = guarantor.relationship?.trim() ?? ''
	if (!firstName && !lastName && !email && !phone) return null
	return { firstName, lastName, relationship, email, phone }
}

/** '2 Children' / '1 Child' header-card chips. */
export function countLabel(
	count: number,
	singular: string,
	plural: string,
): string {
	return `${count} ${count === 1 ? singular : plural}`
}

/** User-authored RentalStep-slug labels (42a0ba9a, merged d815ac5a) — these
 * bytes are truth and win over v1 formatSlug title-casing ('First-time Renter',
 * NOT 'First Time Renter'). */
const RENTAL_STATUS_LABELS: Record<string, string> = {
	'currently-renting': 'Currently Renting',
	'previously-renting': 'Previously Renting',
	'first-time-renter': 'First-time Renter',
	'own-home': 'Own Home',
}

/** 'currently-renting' → 'Currently Renting' — the §11 rental status pill.
 * Fixed labels from the user's d815ac5a merge fix, v1-formatSlug
 * title-casing as the fallback for unknown slugs; null when unset. */
export function rentalStatusLabel(status: string | undefined): string | null {
	if (!status || !status.trim()) return null
	const trimmed = status.trim()
	return (
		RENTAL_STATUS_LABELS[trimmed] ??
		trimmed
			.split('-')
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
			.join(' ')
	)
}

/** v1 EmploymentInfoSection formatEmploymentStatus port (the §11 employment
 * status pill): fixed v1 label strings, default title-cased slug; null when
 * unset (no pill). */
export function employmentStatusLabel(
	status: string | undefined,
): string | null {
	if (!status || !status.trim()) return null
	switch (status) {
		case 'currently-employed':
		// Legacy spelling still present on older applications.
		case 'employed':
			return 'Currently Employed'
		case 'not-employed':
			return 'Not Employed At The Moment'
		case 'no-employment-history':
			return 'No Employment History'
		default:
			return rentalStatusLabel(status)
	}
}

/**
 * v1's previous/additional-employment presence rule (v1:6428-6432 +
 * EmploymentInfoSection hasAnyEmploymentValue): a block renders only when
 * at least one field carries a non-empty value.
 */
export function hasAnyNonEmptyValue(
	record: Record<string, unknown> | undefined,
): boolean {
	if (!record) return false
	return Object.values(record).some(
		(value) =>
			value !== undefined && value !== null && String(value).trim() !== '',
	)
}

/** Tones map 1:1 onto primitives' PastelTone (kept as literals — this module
 * is client-safe and must not import from components). */
export interface ApplicationStatusDisplay {
	label: string
	tone: 'dgreen' | 'red' | 'amber' | 'lamber' | 'neutral' | 'green'
}

/**
 * Status chip for the individual application (detail top bar + applicant
 * switcher). Decision statuses are the landlord-facing terminal states
 * (DETAIL-V2-DECISION-RULES.md); the rest mirror the list page's palette
 * semantics: green = done, amber = moving, neutral = waiting, red = failed.
 */
export function applicationStatusDisplay(
	status: DerivedApplicationStatus,
): ApplicationStatusDisplay {
	switch (status) {
		case 'accepted':
			return { label: 'Accepted', tone: 'dgreen' }
		case 'declined':
			return { label: 'Declined', tone: 'red' }
		case 'withdrawn':
			return { label: 'Withdrawn', tone: 'neutral' }
		case 'abandoned':
			return { label: 'Abandoned', tone: 'neutral' }
		case 'completed':
			return { label: 'Completed', tone: 'green' }
		case 'processing':
			return { label: 'Processing', tone: 'amber' }
		case 'failed':
			return { label: 'Failed', tone: 'red' }
		default:
			// Design 2026-07-13: Incomplete is the light-amber pair
			// (#FFF4E5 / #F59F0A), not gray — it's "waiting on the applicant",
			// a warmer nudge than neutral.
			return { label: 'Incomplete', tone: 'lamber' }
	}
}

/**
 * Applicant-switcher chip tone (2026-07-13 — supersedes design §10's
 * "every chip pale green" note): the chip label stays the RELATIONSHIP, but
 * its color now carries the member's status — green only when the work is
 * done. Guarantors keep the orange family regardless of progress (client:
 * "guarantor in orange is correct" even when incomplete).
 */
export function memberChipTone(
	status: DerivedApplicationStatus,
	applicationType: 'primary' | 'co_applicant' | 'guarantor',
): 'dgreen' | 'damber' | 'amber' | 'lamber' | 'red' | 'neutral' {
	if (applicationType === 'guarantor') return 'damber'
	switch (status) {
		case 'accepted':
		case 'completed':
			return 'dgreen'
		case 'processing':
			return 'amber'
		case 'declined':
		case 'failed':
			return 'red'
		case 'withdrawn':
		case 'abandoned':
			return 'neutral'
		default:
			// incomplete — the light-amber Incomplete pair (#FFF4E5/#F59F0A).
			return 'lamber'
	}
}

/** Avatar ring colour for the member-overview tiles (COMBINED-V2-DATA-MAP §7).
 * `amber` = report processing; `orange` = guarantor states / applicant who
 * needs a guarantor; `gray` = withdrawn/abandoned or not-yet-started. */
export type AvatarRing = 'green' | 'red' | 'orange' | 'amber' | 'gray'

/** Corner badge over the avatar. Circular ✓/✕ for an applicant decision;
 * shield ✓/✕ for a guarantor decision; null for no badge. */
export type AvatarBadge =
	| 'accepted'
	| 'declined'
	| 'guarantor-accepted'
	| 'guarantor-declined'
	| null

export interface AvatarStatusDisplay {
	ring: AvatarRing
	badge: AvatarBadge
}

/**
 * The member-overview avatar's OWN-status ring + corner badge
 * (COMBINED-V2-DATA-MAP §7). Pure: drives the SAME visual language every avatar
 * surface reuses.
 *
 * This reflects the member's OWN application status only. The orange
 * "guarantor recommended / requested" signal is NOT folded in here — it renders
 * as a SEPARATE concentric outer ring around this one (§7.2), so an applicant
 * shows e.g. a green status ring AND an outer orange guarantor ring at once.
 *
 * Guarantors: ring is ALWAYS orange; the badge carries the landlord decision
 * (green/red shield). Applicants:
 *   - accepted → green + ✓ / declined → red + ✕ (terminal decision);
 *   - completed → green, incomplete → red, processing → amber,
 *     withdrawn/abandoned → gray, failed → red.
 */
export function avatarStatusDisplay(
	status: DerivedApplicationStatus,
	options: { isGuarantor: boolean },
): AvatarStatusDisplay {
	if (options.isGuarantor) {
		if (status === 'accepted')
			return { ring: 'orange', badge: 'guarantor-accepted' }
		if (status === 'declined')
			return { ring: 'orange', badge: 'guarantor-declined' }
		return { ring: 'orange', badge: null }
	}

	if (status === 'accepted') return { ring: 'green', badge: 'accepted' }
	if (status === 'declined') return { ring: 'red', badge: 'declined' }

	switch (status) {
		case 'completed':
			return { ring: 'green', badge: null }
		case 'processing':
			return { ring: 'amber', badge: null }
		case 'withdrawn':
		case 'abandoned':
			return { ring: 'gray', badge: null }
		// 'incomplete' (started, missing info) and 'failed' both read red.
		default:
			return { ring: 'red', badge: null }
	}
}

/**
 * Byte count → the size line under an uploaded document's name ("245 KB",
 * "1.4 MB"). Returns null for anything that can't be a real size — absent,
 * negative, non-finite — so the caller falls back to the upload's status text
 * rather than printing a meaningless "0 KB".
 *
 * KB and up are the only units shown: a sub-kilobyte upload is a rounding
 * curiosity, not information the landlord acts on, so it floors at "1 KB".
 * Whole numbers below 10 MB, one decimal above, which keeps the line short
 * while still separating a 1.4 MB scan from a 9.8 MB one.
 */
export function formatDocumentSize(bytes: unknown): string | null {
	if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes <= 0) {
		return null
	}
	const kb = bytes / 1024
	if (kb < 1024) return `${Math.max(1, Math.round(kb))} KB`
	const mb = kb / 1024
	if (mb < 10) return `${Math.round(mb * 10) / 10} MB`
	if (mb < 1024) return `${Math.round(mb)} MB`
	return `${Math.round((mb / 1024) * 10) / 10} GB`
}
